import { ref } from "vue";
import { defaultApi, type IResponse } from "@/api";
import { logError, logInfo, logEvent, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";
import type { WidgetManagementRequest } from "@/views/ControlTowerView/types";

export const useDashboardManagement = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);
  const dashboards = ref<any[]>([]);

  /**
   * Fetches all dashboards from the API
   * @returns Promise<IResponse> The API response containing dashboard data
   */
  const fetchDashboards = async (): Promise<IResponse> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Fetching dashboards from API");

    try {
      loading.value = true;
      error.value = null;
      const response = await defaultApi.apiVersion1.dashboards.getAll<IResponse>({ params: { limit: 25 } });

      if (response.data) {
        dashboards.value = Array.isArray(response.data) ? response.data : [];
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboards fetched successfully. Count: ${dashboards.value.length}`);
      } else {
        logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, "Dashboards API call completed but no data received");
      }

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to fetch dashboards";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboards/getAll",
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching dashboards: ${errorMessage}`);

      throw err;
    } finally {
      loading.value = false;
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboards/getAll"
      });
    }
  };

  /**
   * Fetches dashboards meta data using dashboard ID
   * @param dashboardId - The ID of the dashboard to fetch metadata for
   * @returns Promise<IResponse> The API response containing dashboard data
   */
  const fetchDashboardMeta = async (dashboardId: string) => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Fetching metadata for dashboard ID: ${dashboardId}`);

    try {
      loading.value = true;
      error.value = null;
      const response = await defaultApi.apiVersion1.dashboards.getOne(dashboardId);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard metadata fetched successfully for ID: ${dashboardId}`);

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || `Failed to fetch metadata for dashboard ID: ${dashboardId}`;
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboards/getOne",
        dashboardId,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error fetching dashboard metadata: ${errorMessage}`);

      throw err;
    } finally {
      loading.value = false;
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboards/getOne",
        dashboardId
      });
    }
  };

  /**
   * Updates the order of widgets in a dashboard (drag and drop within dashboard)
   * @param dashboardId - The ID of the dashboard to update
   * @param widgetObjects - Array of widget objects with their layout positions
   * @returns Promise<IResponse> The API response
   */
  const updateWidgetsOrder = async (dashboardId: string, widgetObjects: any): Promise<IResponse> => {
    const apiStart = performance.now();
    logInfo(
      FEATURE_NAMES.MVS_CONTROL_TOWER,
      `Updating widget order for dashboard ${dashboardId}. Widget count: ${widgetObjects.length}`
    );

    try {
      loading.value = true;
      error.value = null;

      // Prepare the payload with widget IDs and their layout positions
      const payload = {
        dashboard_id: dashboardId,
        widgets: widgetObjects
      };

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Request payload: ${JSON.stringify(payload, null, 2)}`);

      const response = await defaultApi.apiVersion1.updateWidget.patch("layouts", payload);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget order updated successfully for dashboard ${dashboardId}`);

      return response as IResponse;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to update widget order";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "widgets/patch/layouts",
        dashboardId,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error updating widget order: ${errorMessage}`);

      throw err;
    } finally {
      loading.value = false;
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "widgets/patch/layouts",
        dashboardId
      });
    }
  };

  /**
   * Updates widgets for an existing dashboard (also used for bulk updates)
   * This is a unified method that handles:
   * - Updating dashboard name/widgets (manage dashboard modal)
   * - Bulk position updates (repositioning after add/delete)
   * - Any other dashboard property updates via PATCH /dashboards
   *
   * @param dashboardIdOrPayload - Either dashboardId (string) for backward compatibility, or payload object for bulk updates
   * @param payload - The widget management request (optional if first param is payload object)
   * @returns Promise<IResponse> The API response
   */
  const updateDashboardWidgets = async (
    dashboardIdOrPayload: string | WidgetManagementRequest,
    payload?: WidgetManagementRequest
  ): Promise<IResponse> => {
    const apiStart = performance.now();

    // Handle both call signatures:
    // 1. updateDashboardWidgets(dashboardId, payload) - original usage
    // 2. updateDashboardWidgets({ dashboards: [...] }) - bulk update usage
    let dashboardId: string;
    let requestPayload: WidgetManagementRequest;
    let isBulkUpdate = false;

    if (typeof dashboardIdOrPayload === "string") {
      // Original signature: (dashboardId, payload)
      dashboardId = dashboardIdOrPayload;
      requestPayload = payload!;
    } else {
      // Bulk update signature: (payload)
      isBulkUpdate = true;
      dashboardId = dashboardIdOrPayload.dashboards?.[0]?.dashboard_id || "";
      requestPayload = dashboardIdOrPayload;
    }

    const dashboardIds = requestPayload.dashboards?.map((d) => d.dashboard_id).join(", ") || "";
    const logMessage = isBulkUpdate
      ? `Bulk updating ${requestPayload.dashboards?.length || 0} dashboard(s) - IDs: ${dashboardIds}`
      : `Updating widgets for dashboard: ${dashboardId}`;

    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, logMessage);

    try {
      loading.value = true;
      error.value = null;

      // API endpoint: PATCH /isce/v1/dashboard-widget/dashboards
      const response = await defaultApi.apiVersion1.dashboards.patch<IResponse>("", requestPayload);

      const successMessage = isBulkUpdate
        ? `Bulk update successful - ${requestPayload.dashboards?.length || 0} dashboard(s) updated`
        : `Dashboard widgets updated successfully: ${dashboardId}`;

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, successMessage);

      return response;
    } catch (err: any) {
      const errorMessage =
        err?.message || (isBulkUpdate ? "Failed to bulk update dashboards" : "Failed to update dashboard widgets");
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboard-widget/dashboards/widgets/update",
        dashboardId,
        error: errorMessage,
        statusCode: err?.response?.status
      });

      const errorLog = isBulkUpdate
        ? `Error in bulk update dashboards: ${err?.message || "Unknown error"}`
        : `Error updating dashboard widgets: ${errorMessage}`;

      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, errorLog);

      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboard-widget/dashboards/widgets/update",
        dashboardId
      });
      loading.value = false;
    }
  };

  /**
   * Deletes a dashboard using its ID
   * @param dashboardId - The ID of the dashboard to delete
   * @returns Promise<null> The API response for the delete operation (returns null on success)
   */
  const deleteDashboard = async (dashboardId: string): Promise<null> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Deleting dashboard ID: ${dashboardId}`);

    try {
      loading.value = true;
      error.value = null;
      const response = await defaultApi.apiVersion1.dashboards.deleteOne(dashboardId);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard deleted successfully for ID: ${dashboardId}`);

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || `Failed to delete dashboard ID: ${dashboardId}`;
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboards/deleteOne",
        dashboardId,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error deleting dashboard: ${errorMessage}`);
      throw err;
    } finally {
      loading.value = false;
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboards/deleteOne",
        dashboardId
      });
    }
  };
  /**
   * Updates filters for an existing dashboard
   * @param dashboardId - The ID of the dashboard to update filters for
   * @param payload - The  request containing filters to be applied to dashboard widgets
   * @returns Promise<IResponse> The API response
   */
  const updateDashboardFilters = async (dashboardId: string, payload: unknown): Promise<IResponse> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Updating filters for dashboard: ${dashboardId}`);

    try {
      // Validate dashboardId
      if (!dashboardId) {
        throw new Error("Dashboard ID is required for updating filters");
      }

      // API endpoint: PUT /isce/v1/dashboard-widget/dashboards/{dashboardId}/filters
      // Using putOne with subpath to create the correct endpoint
      const response = await defaultApi.apiVersion1.dashboards.putOne<IResponse>(
        dashboardId,
        payload,
        undefined,
        "filters"
      );

      logInfo(
        FEATURE_NAMES.MVS_CONTROL_TOWER,
        `Dashboard filters updated successfully: ${dashboardId} with updated filters payload: ${JSON.stringify(payload)}`
      );

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to save  dashboard filters";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboard-widget/dashboards/filters/update",
        dashboardId,
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error updating dashboard filters: ${errorMessage}`);

      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboard-widget/dashboards/filters/update",
        dashboardId
      });
    }
  };

  return {
    loading,
    error,
    dashboards,
    fetchDashboards,
    fetchDashboardMeta,
    updateWidgetsOrder,
    updateDashboardWidgets,
    deleteDashboard,
    // Alias for backward compatibility - both point to the same function
    bulkUpdateDashboards: updateDashboardWidgets,
    updateDashboardFilters
  };
};
